import '@reach/combobox/styles.css';

import React, { ChangeEvent, useEffect, useState } from 'react';

import {
  Combobox,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
  ComboboxPopover,
} from '@reach/combobox';
import type { RegisterOptions, UseFormRegister } from 'react-hook-form';

import './ComboBox.css';

type ComboBoxProps = {
  ariaLabel: string;
  /** Forwarded to the input so an external <label htmlFor> can target it. */
  id?: string;
  name: string;
  placeholder?: string;
  options: string[];
  register: UseFormRegister<any>;
  validationOptions?: RegisterOptions<any>;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
};

export const ComboBox = React.forwardRef<HTMLInputElement, ComboBoxProps>(
  function ComboBoxComponent({
    ariaLabel,
    id,
    name,
    placeholder,
    options,
    register,
    validationOptions,
    'aria-invalid': ariaInvalid,
  }) {
    const [currentOptions, setCurrentOptions] = useState(options);

    useEffect(() => {
      setCurrentOptions(options);
    }, [options]);

    // Compose react-hook-form's change handler with the suggestion filter —
    // overriding it would sever RHF's change pipeline for this field (no
    // watch() updates, no revalidation/error clearing while typing).
    const { onChange: registerOnChange, ...registerRest } = register(name, validationOptions);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      void registerOnChange(e);
      const userInput = e.currentTarget.value;
      const filteredOptions = options.filter((suggestion) =>
        suggestion.toLowerCase().startsWith(userInput.toLowerCase())
      );
      setCurrentOptions(filteredOptions);
    };

    return (
      <Combobox aria-label={ariaLabel} className="combobox-2024">
        <ComboboxInput
          id={id}
          placeholder={placeholder}
          aria-invalid={ariaInvalid}
          {...registerRest}
          onChange={onChange}
        />
        <ComboboxPopover>
          <ComboboxList>
            {currentOptions.map((option) => (
              <ComboboxOption key={option} value={option} />
            ))}
          </ComboboxList>
        </ComboboxPopover>
      </Combobox>
    );
  }
);

ComboBox.displayName = 'ComboBox';
